import { useState } from 'react'
import { useForm, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Upload, Download, AlertCircle } from 'lucide-react'

interface NewImportProps {
  data_sources_hint: string[]
}

export default function NewOrganizationDonorImport({ data_sources_hint }: NewImportProps) {
  const [fileName, setFileName] = useState<string | null>(null)
  const form = useForm<{ file: File | null; data_source: string }>({
    file: null,
    data_source: data_sources_hint[0] ?? 'cci_tunis',
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    setFileName(f?.name ?? null)
    form.setData('file', f)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post('/people/imports/organization_donors', {
      forceFormData: true,
    })
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Importer des organisations donatrices</h1>
        <p className="text-sm text-muted-foreground">
          Téléversez un fichier XLSX avec la structure attendue. Le système validera la structure et les données avant tout import.
        </p>
      </header>

      <div className="rounded-md border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">Structure verrouillée</p>
            <p className="text-xs text-muted-foreground">
              Les en-têtes doivent correspondre exactement au modèle. Téléchargez-le si besoin.
            </p>
          </div>
          <a
            href="/people/imports/organization_donors/template"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Download className="h-4 w-4" />
            Télécharger le modèle
          </a>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="data_source">Source des données</Label>
          <Input
            id="data_source"
            type="text"
            value={form.data.data_source}
            onChange={(e) => form.setData('data_source', e.target.value)}
            list="data-sources-list"
            placeholder="Ex : cci_tunis"
            required
          />
          {data_sources_hint.length > 0 && (
            <datalist id="data-sources-list">
              {data_sources_hint.map((src) => (
                <option key={src} value={src} />
              ))}
            </datalist>
          )}
          {form.errors.data_source && (
            <p className="text-sm text-destructive">{form.errors.data_source}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="file">Fichier XLSX</Label>
          <div className="flex items-center gap-3 rounded-md border-2 border-dashed border-muted-foreground/25 p-6">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <div className="flex-1">
              <Input
                id="file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                required
              />
              {fileName && <p className="mt-1 text-xs text-muted-foreground">{fileName}</p>}
            </div>
          </div>
          {form.errors.file && (
            <p className="flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {form.errors.file}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Link href="/people/donors" className="text-sm text-muted-foreground hover:underline">
            Annuler
          </Link>
          <Button type="submit" disabled={form.processing || !form.data.file}>
            {form.processing ? 'Analyse en cours…' : "Téléverser et prévisualiser"}
          </Button>
        </div>
      </form>
    </div>
  )
}
